import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { services } from '@/mocks/services';

const ServicesSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={ref} className="py-20 md:py-28 bg-brand-dark">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-brand-teal/30 rounded-full mb-4">
            <span className="text-xs font-semibold text-brand-teal-light uppercase tracking-widest">What We Build</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            Expert Masonry &amp;{' '}
            <span className="text-brand-teal-light italic">Construction Services</span>
          </h2>
          <p className="text-white/60 text-base max-w-2xl mx-auto">
            From intricate stonework to large-scale commercial builds, Valley View Construction delivers superior craftsmanship across every project category throughout the Michiana area.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {services.map((svc, idx) => (
            <Link
              to={`/services/${svc.id}`}
              key={svc.id}
              className={`group relative overflow-hidden rounded-2xl h-64 transition-all duration-700 cursor-pointer ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${idx * 60}ms` }}
            >
              {/* Background Image */}
              <img
                src={svc.image}
                alt={`${svc.name} - Valley View Construction Elkhart Indiana`}
                className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-brand-black/90 via-brand-black/40 to-transparent group-hover:from-brand-black/80 transition-all duration-300" />
              <div className="absolute inset-0 bg-brand-teal/0 group-hover:bg-brand-teal/10 transition-all duration-300" />

              {/* Category Badge */}
              <div className="absolute top-3 left-3 px-2.5 py-1 bg-brand-teal/90 rounded-md">
                <span className="text-xs font-semibold text-white uppercase tracking-wide">{svc.category}</span>
              </div>

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`${svc.icon} text-brand-teal-light text-base`} />
                      </div>
                      <h3 className="text-white font-semibold text-base leading-tight">{svc.name}</h3>
                    </div>
                    <p className="text-white/60 text-xs leading-relaxed line-clamp-2">{svc.shortDesc}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-brand-teal-light text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                  <span>Learn More</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 px-8 py-4 border-2 border-brand-teal text-brand-teal-light hover:bg-brand-teal hover:text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap"
          >
            View All Services
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
